/**
 * Confirm With Name Dialog Component
 *
 * A confirmation dialog that requires the user to type the exact name
 * of the item they want to delete for safety.
 *
 * Features:
 * - Requires exact name match to enable deletion
 * - Loading state support
 * - Destructive variant styling with warning icon
 * - Customizable labels and descriptions
 * - Optional metadata display (created date, record count, file size, etc.)
 * - Optional warning message for dependencies
 */

import { useState, useEffect } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '@loader/ui';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@loader/ui';
import { Input } from '@loader/ui';
import { Label } from '@loader/ui';

/**
 * Metadata item for displaying key-value pairs
 */
interface MetadataItem {
  /** Label for the metadata field */
  label: string;
  /** Value to display */
  value: string;
}

/**
 * Props for ConfirmWithNameDialog component
 */
interface ConfirmWithNameDialogProps {
  /** Whether the dialog is open */
  open: boolean;
  /** Callback when dialog open state changes */
  onOpenChange: (open: boolean) => void;
  /** Dialog title (e.g., "Delete Table") */
  title: string;
  /** Description text before the name input */
  description?: string;
  /** Name of the item to delete (user must type this exactly) */
  itemName: string;
  /** Label for the item type (e.g., "Table", "Version", "Linked Table") */
  itemType: string;
  /** Optional metadata to display about the item (e.g., created date, record count, size) */
  metadata?: MetadataItem[];
  /** Optional warning message about dependencies (e.g., "This will also delete 3 linked tables") */
  warningMessage?: string;
  /** Text for the confirm button (default: "Delete") */
  confirmText?: string;
  /** Text for the cancel button (default: "Cancel") */
  cancelText?: string;
  /** Whether the operation is in progress */
  isLoading?: boolean;
  /** Loading state text (default: "Deleting...") */
  loadingText?: string;
  /** Callback when user confirms deletion */
  onConfirm: () => void | Promise<void>;
}

/**
 * ConfirmWithNameDialog Component
 *
 * Displays a confirmation dialog that requires the user to type the exact
 * name of the item they want to delete. The confirm button is disabled
 * until the name matches exactly.
 *
 * @example
 * ```tsx
 * <ConfirmWithNameDialog
 *   open={deleteDialogOpen}
 *   onOpenChange={setDeleteDialogOpen}
 *   title="Delete Table"
 *   itemName={table.name}
 *   itemType="Table"
 *   metadata={[
 *     { label: 'Created', value: 'Jan 15, 2024' },
 *     { label: 'Records', value: '1,234' },
 *     { label: 'Size', value: '2.5 MB' }
 *   ]}
 *   warningMessage="This will also delete 3 linked tables"
 *   isLoading={isDeleting}
 *   onConfirm={handleConfirmDelete}
 * />
 * ```
 */
export function ConfirmWithNameDialog({
  open,
  onOpenChange,
  title,
  description,
  itemName,
  itemType,
  metadata,
  warningMessage,
  confirmText = 'Delete',
  cancelText = 'Cancel',
  isLoading = false,
  loadingText = 'Deleting...',
  onConfirm,
}: ConfirmWithNameDialogProps) {
  // Local state for the confirmation input
  const [confirmName, setConfirmName] = useState('');

  // Reset confirmation input when dialog closes
  useEffect(() => {
    if (!open) {
      setConfirmName('');
    }
  }, [open]);

  // Check if the confirm button should be enabled
  const isConfirmEnabled =
    !isLoading && confirmName.trim() === itemName.trim();

  // Handle confirm action
  const handleConfirm = async () => {
    if (!isConfirmEnabled) return;
    await onConfirm();
  };

  // Handle cancel action
  const handleCancel = () => {
    if (!isLoading) {
      onOpenChange(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            {title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Warning message for dependencies */}
          {warningMessage && (
            <div className="bg-destructive/10 border-destructive/20 rounded-md border p-3">
              <p className="text-destructive text-sm font-medium">
                {warningMessage}
              </p>
            </div>
          )}

          {/* Metadata display */}
          {metadata && metadata.length > 0 && (
            <div className="bg-muted/50 space-y-2 rounded-md border p-3">
              <p className="text-muted-foreground text-xs font-medium uppercase">
                Item Details
              </p>
              <dl className="space-y-1">
                {metadata.map((item, index) => (
                  <div key={index} className="flex justify-between text-sm">
                    <dt className="text-muted-foreground">{item.label}:</dt>
                    <dd className="font-medium">{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          {/* Description and confirmation prompt */}
          <p className="text-muted-foreground text-sm">
            {description ||
              `This action cannot be undone. Please type the ${itemType.toLowerCase()} name`}
            <span className="font-semibold"> {itemName}</span> to confirm
            deletion.
          </p>

          {/* Name confirmation input */}
          <div>
            <Label htmlFor="confirmName">{itemType} name</Label>
            <Input
              id="confirmName"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={itemName}
              disabled={isLoading}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && isConfirmEnabled) {
                  void handleConfirm();
                }
              }}
              autoFocus
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleCancel}
            disabled={isLoading}
          >
            {cancelText}
          </Button>
          <Button
            variant="destructive"
            onClick={() => void handleConfirm()}
            disabled={!isConfirmEnabled}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {loadingText}
              </>
            ) : (
              confirmText
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

